import React, { useEffect, useState, useCallback, useMemo } from "react";
import { api, API } from "@/lib/auth";
import { PageHeader } from "@/pages/Classes";
import { Link } from "react-router-dom";
import { Clock, MapPin, User, RefreshCcw, ChevronLeft, ChevronRight, CalendarRange, CalendarPlus } from "lucide-react";
import { fmtShort, fmtTime, fmtRange, crewDayList } from "@/lib/enrol";

const DAYS = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];
const FULL_DAY = { Mon: "monday", Tue: "tuesday", Wed: "wednesday", Thu: "thursday", Fri: "friday", Sat: "saturday", Sun: "sunday" };
const STATUS = { available: ["AVAILABLE","status-available"], filling_fast: ["FILLING FAST","status-filling"], almost_full: ["ALMOST FULL","status-almost"], full: ["FULL","status-full"] };

const ymd = (d) => d.toISOString().slice(0, 10);
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const mondayOf = (d) => { const x = new Date(d); x.setHours(12, 0, 0, 0); return addDays(x, -((x.getDay() + 6) % 7)); };
const dayKey = (s) => { const d = (s.day || "").slice(0, 3).toLowerCase(); return DAYS.find(x => x.toLowerCase() === d) || null; };
const timeKey = (s) => (s || "").padStart(5, "0");
const dateOnly = (v) => { if (!v) return null; const t = Date.parse(v); return isNaN(t) ? null : ymd(new Date(t)); };

// Does this class row run on a given calendar date?
const runsOn = (s, dateStr) => {
  const k = dayKey(s); if (!k) return false;
  if (DAYS[(new Date(dateStr + "T12:00:00").getDay() + 6) % 7] !== k) return false;
  const sc = s.schedule || {};
  if (sc.first_class && dateStr < sc.first_class) return false;
  if (sc.last_class && dateStr > sc.last_class) return false;
  return !(sc.skip_dates || []).includes(dateStr);
};

const pickDefaultTerm = (terms, sessions) => {
  const today = ymd(new Date());
  const has = (t) => sessions.some(s => s.term_id === t.id);
  const live = terms.filter(t => t.active !== false).sort((a, b) => (a.start_date || "").localeCompare(b.start_date || ""));
  return live.find(t => (t.start_date || "") <= today && today <= (t.end_date || "9999") && has(t))
    || live.find(t => (t.end_date || "9999") >= today && has(t))
    || live.find(t => (t.end_date || "9999") >= today) || live[live.length - 1] || null;
};

function ClassCard({ item }) {
  const [label, cls] = STATUS[item.availability_status] || STATUS.available;
  const full = item.availability_status === "full";
  return (
    <div className="border-l-2 border-red-600 pl-3 py-2 mb-2 hover:bg-white/[0.03]" data-testid={`class-${item.id}`}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest text-red-500 font-bold">Class</div>
        <span className={`text-[9px] font-bold px-1.5 py-0.5 border ${cls}`}>{label}</span>
      </div>
      <div className="font-bold text-sm mt-1">{item.batch_name}</div>
      <div className="flex items-center gap-2 text-xs text-zinc-400 mt-1"><Clock size={11}/>{fmtRange(item.start_time, item.end_time)}</div>
      {item.location_name && <div className="flex items-center gap-2 text-xs text-zinc-500"><MapPin size={11}/>{item.location_name}</div>}
      {item.instructor && <div className="flex items-center gap-2 text-xs text-zinc-500"><User size={11}/>{item.instructor}</div>}
      <div className="mt-2 flex items-center justify-between gap-2">
        {full
          ? <Link to={`/classes/${item.batch_id}`} className="text-zinc-400 text-[11px] uppercase tracking-widest py-2.5 -my-1 inline-flex items-center">Pre-register</Link>
          : <Link to={`/register/${item.id}`} className="text-red-500 text-[11px] uppercase tracking-widest font-bold py-2.5 -my-1 inline-flex items-center" data-testid={`register-${item.id}`}>Register →</Link>}
        <a href={`${API}/sessions/${item.id}/calendar.ics`} title="Add to calendar" className="text-zinc-500 hover:text-red-500 transition-colors p-2.5 -m-1.5 inline-flex" data-testid={`calendar-${item.id}`}><CalendarPlus size={16}/></a>
      </div>
    </div>
  );
}

function EventCard({ item }) {
  const isW = item.kind === "workshop", isC = item.kind === "crew";
  const href = isC ? `/competition-team?apply=1&crew=${item.id}` : isW ? "/workshops" : `/events/${item.id}`;
  const color = isC ? "border-yellow-400" : isW ? "border-amber-400" : "border-sky-400";
  const text = isC ? "text-yellow-400" : isW ? "text-amber-400" : "text-sky-400";
  return (
    <Link to={href} className={`block border-l-2 ${color} pl-3 py-2 mb-2 hover:bg-white/[0.03]`} data-testid={`${item.kind}-${item.id}`}>
      <div className={`text-[10px] uppercase tracking-widest font-bold ${text}`}>{isC ? "Competition crew · audition only" : isW ? "Workshop" : "Event"}</div>
      <div className="font-bold text-sm mt-1">{item.name || item.title}{isC && item.age_range ? <span className="text-zinc-500 font-normal"> · {item.age_range}</span> : null}</div>
      <div className="flex items-center gap-2 text-xs text-zinc-400 mt-1"><Clock size={11}/>{isC && item.start_time ? fmtRange(item.start_time, item.end_time) : item.start_time ? fmtTime(item.start_time) : fmtShort(item.date)}</div>
      {item.location_name && <div className="flex items-center gap-2 text-xs text-zinc-500"><MapPin size={11}/>{item.location_name}</div>}
    </Link>
  );
}

export default function Timetable() {
  const [sessions, setSessions] = useState([]);
  const [locations, setLocations] = useState([]);
  const [batches, setBatches] = useState([]);
  const [terms, setTerms] = useState([]);
  const [workshops, setWorkshops] = useState([]);
  const [events, setEvents] = useState([]);
  const [crews, setCrews] = useState([]);
  const [f, setF] = useState({ batch: "", location: "" });
  const [view, setView] = useState("week");
  const [termId, setTermId] = useState("");
  const [weekStart, setWeekStart] = useState(() => mondayOf(new Date()));
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const [se, lo, ba, te, wo, ev, cr] = await Promise.all([
        api.get("/sessions"), api.get("/locations"), api.get("/batches"), api.get("/terms"), api.get("/workshops"), api.get("/events").catch(() => ({ data: [] })), api.get("/crews").catch(() => ({ data: [] })),
      ]);
      setSessions(se.data || []); setLocations(lo.data || []); setBatches(ba.data || []); setTerms(te.data || []);
      setWorkshops(wo.data || []); setEvents((ev.data || []).filter(x => x.active !== false)); setCrews(cr.data || []);
    } finally { setRefreshing(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(load, 60_000);
    window.addEventListener("focus", load);
    return () => { clearInterval(t); window.removeEventListener("focus", load); };
  }, [load]);
  useEffect(() => { if (!termId && terms.length) { const t = pickDefaultTerm(terms, sessions); if (t) setTermId(t.id); } }, [terms, sessions, termId]);

  const term = terms.find(t => t.id === termId) || null;
  const filtered = useMemo(() => sessions.filter(s => s.active !== false
    && (!f.batch || s.batch_id === f.batch) && (!f.location || s.location_id === f.location)
    && (!termId || !s.term_id || s.term_id === termId)), [sessions, f, termId]);

  const weekDates = DAYS.map((_, i) => ymd(addDays(weekStart, i)));
  const todayStr = ymd(new Date());
  const isThisWeek = weekDates.includes(todayStr);

  const crewItems = (day) => (!f.batch ? crews : []).filter(c => crewDayList(c).some(d => dayKey({ day: d }) === day) && (!f.location || !c.location_id || c.location_id === f.location))
    .map(c => ({ kind: "crew", ...c, _sort: timeKey(c.start_time || "00:00") }));

  // Column contents
  const cols = DAYS.map((day, i) => {
    const dateStr = weekDates[i];
    let items;
    if (view === "week") {
      const pool = termId ? sessions.filter(s => s.active !== false && (!f.batch || s.batch_id === f.batch) && (!f.location || s.location_id === f.location)) : filtered;
      items = pool.filter(s => runsOn(s, dateStr)).map(s => ({ kind: "class", ...s, _sort: timeKey(s.start_time) }));
      crewItems(day).forEach(c => items.push(c));
      workshops.filter(w => dateOnly(w.date) === dateStr && (!f.location || !w.location_id || w.location_id === f.location)).forEach(w => items.push({ kind: "workshop", ...w, _sort: timeKey(w.start_time || "00:00") }));
      events.filter(e => dateOnly(e.date) === dateStr).forEach(e => items.push({ kind: "event", ...e, _sort: timeKey(e.start_time || "00:00") }));
    } else {
      items = filtered.filter(s => dayKey(s) === day).map(s => ({ kind: "class", ...s, _sort: timeKey(s.start_time) }));
      crewItems(day).forEach(c => items.push(c));
    }
    items.sort((a, b) => a._sort.localeCompare(b._sort));
    return { day, dateStr, items };
  });

  const termRows = filtered.filter(s => s.term_id === termId);
  const firsts = termRows.map(s => s.schedule?.first_class).filter(Boolean).sort();
  const lasts = termRows.map(s => s.schedule?.last_class).filter(Boolean).sort();
  const skips = [...new Set(termRows.flatMap(s => s.schedule?.skip_dates || []))].sort();
  const sel = "bg-black border border-white/10 px-3 sm:px-4 py-2 text-sm focus:border-red-600 outline-none w-full sm:w-auto min-w-0";

  return (
    <>
      <PageHeader eyebrow={term ? term.name : "Timetable"} title="Timetable" sub={view === "week" ? "What's actually on this week — every batch, location and time." : "The full weekly pattern for the selected term."} />
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6 grid grid-cols-2 sm:flex sm:flex-wrap items-center gap-2 sm:gap-3">
        <div className="flex border border-white/10 col-span-2 sm:col-span-1 [&>button]:flex-1" data-testid="view-toggle">
          {[["week", "This week"], ["term", "This term"]].map(([k, l]) => (
            <button key={k} onClick={() => setView(k)} className={`px-4 py-2 text-xs uppercase tracking-widest font-bold ${view === k ? "bg-red-600 text-white" : "text-zinc-400 hover:text-white"}`} data-testid={`view-${k}`}>{l}</button>
          ))}
        </div>
        <select value={termId} onChange={e => setTermId(e.target.value)} className={sel} data-testid="filter-term">
          {terms.filter(t => t.active !== false).sort((a, b) => (a.start_date || "").localeCompare(b.start_date || "")).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <select value={f.batch} onChange={e => setF({...f, batch: e.target.value})} className={sel} data-testid="filter-batch">
          <option value="">All batches</option>{batches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
        <select value={f.location} onChange={e => setF({...f, location: e.target.value})} className={sel} data-testid="filter-location">
          <option value="">All locations</option>{locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
        {view === "week" && (
          <div className="flex items-center gap-1 col-span-2 sm:col-span-1 sm:ml-auto flex-wrap" data-testid="week-nav">
            <button onClick={() => setWeekStart(w => addDays(w, -7))} className="p-2 border border-white/10 hover:border-red-600" aria-label="Previous week" data-testid="week-prev"><ChevronLeft size={14}/></button>
            <button onClick={() => setWeekStart(mondayOf(new Date()))} className={`px-3 py-2 text-xs uppercase tracking-widest border border-white/10 ${isThisWeek ? "text-zinc-600" : "hover:border-red-600"}`} disabled={isThisWeek} data-testid="week-today">Today</button>
            <button onClick={() => setWeekStart(w => addDays(w, 7))} className="p-2 border border-white/10 hover:border-red-600" aria-label="Next week" data-testid="week-next"><ChevronRight size={14}/></button>
            <span className="text-xs text-zinc-400 ml-2" data-testid="week-range">{fmtShort(weekDates[0])} – {fmtShort(weekDates[6])}</span>
          </div>
        )}
        <button onClick={load} disabled={refreshing} className={`text-zinc-500 hover:text-red-500 disabled:opacity-50 flex items-center gap-1 text-xs ${view === "week" ? "" : "ml-auto"}`} data-testid="refresh-timetable">
          <RefreshCcw size={12} className={refreshing ? "animate-spin" : ""}/>{refreshing ? "Refreshing" : "Refresh"}
        </button>
      </div>

      {term && (
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 pb-4">
          <div className="card-surface px-5 py-3 flex flex-wrap items-center gap-x-6 gap-y-1 text-sm text-zinc-300" data-testid="term-summary">
            <span className="flex items-center gap-2 text-red-500 font-bold uppercase tracking-widest text-xs"><CalendarRange size={14}/>{term.name}</span>
            {term.classes_count && <span>{term.classes_count} classes</span>}
            <span>First class {fmtShort(firsts[0] || term.start_date)}</span>
            <span>Last class {fmtShort(lasts[lasts.length - 1] || term.end_date)}</span>
            {skips.length > 0 && <span className="text-amber-400" data-testid="term-skips">No class: {skips.map(fmtShort).join(", ")}</span>}
          </div>
        </div>
      )}

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 pb-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-3" data-testid="timetable-grid">
          {cols.map(({ day, dateStr, items }) => {
            const isToday = view === "week" && dateStr === todayStr;
            const noClass = view === "week" && skips.includes(dateStr);
            return (
              <div key={day} className={`card-surface ${items.length === 0 ? "p-3 md:p-4" : "p-4"} ${isToday ? "ring-1 ring-red-600" : ""}`} data-testid={`day-col-${day.toLowerCase()}`}>
                <div className={`flex items-baseline justify-between ${items.length === 0 ? "mb-0 md:mb-3 md:border-b md:border-white/10 md:pb-2" : "mb-3 border-b border-white/10 pb-2"}`}>
                  <div>
                    <div className="font-display text-lg uppercase">{day}</div>
                    {view === "week" && <div className={`text-[11px] ${isToday ? "text-red-500 font-bold" : "text-zinc-500"}`} data-testid={`day-date-${day.toLowerCase()}`}>{fmtShort(dateStr)}{isToday ? " · Today" : ""}</div>}
                  </div>
                  <div className="text-zinc-500 text-xs">{items.length === 0 ? <span className={noClass ? "text-amber-400" : ""}>{noClass ? "No class (holiday)" : "Rest day"}</span> : items.length}</div>
                </div>
                {items.length === 0 && <div className="hidden md:block text-zinc-600 text-xs py-6 text-center">{noClass ? "No class (holiday)" : "Rest day"}</div>}
                {items.map(item => item.kind === "class" ? <ClassCard key={item.id} item={item} /> : <EventCard key={`${item.kind}-${item.id}`} item={item} />)}
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}
